import { Logtail } from '@logtail/browser';


const labels = { DEFAULT: 'Generic' };
let logtail = null;
let sourceAppName = null;
let globalMetadata = {};
const ENV = process.env.NODE_ENV;

const UILogging = {
  labels,

  /**
   * Initialize the logger for the browser
   * @param {string} token - Logtail source token
   * @param {string} [host] - Logtail endpoint host
   * @param {string} [appName] - Optional application name
   * @param {object} [newLabels] - Optional object to merge into labels
   * @param {object} [metadata] - Optional metadata to include with all log entries
   */
  init: (token, host, appName, newLabels = {}, metadata = {}) => {
    Object.assign(UILogging.labels, newLabels);
    sourceAppName = appName;
    
    // Store global metadata
    globalMetadata = { ...metadata };

    if (!token || !host) {
      console.error('BetterStack ENV variables missing.');
      return;
    }

    logtail = new Logtail(token, host ? { endpoint: `https://${host}` } : undefined);
  },

  /**
   * Internal log method to centralize log sending
   * @param {'info'|'error'|'warn'|'debug'} level
   * @param {string|null} label
   * @param {string} message
   * @param {object|null} object
   * @param {object|null} metadata - Additional metadata to include with this log entry
   */
  _log: (level, label = null, message, object = null, metadata = null) => {
    if (!logtail) {
      console.error('Logger not initialized.');
      return;
    }
    const usedLabel = label || labels.DEFAULT;
    const fullMessage = `[${ENV}]-(${usedLabel}): ${message}`.trim();
    // Merge global metadata with call-specific metadata
    const mergedMetadata = { ...globalMetadata, ...(metadata || {}) };
    
    const logOptions = {
      appName: sourceAppName,
      loggedObject: object,
      metadata: mergedMetadata
    };
    try {
      if (typeof logtail[level] === 'function') {
        logtail[level](fullMessage, logOptions);
      } else {
        logtail.info(fullMessage, logOptions);
      }
    } catch (err) {
      console.error(`Logtail ${level} failed:`, err.message);
    }
  },

  /**
   * Logs info to betterstack
   * @param {logging.labels} [label] - Predefined label for the logs, defaults to Generic
   * @param {string} message - The log itself
   * @param {Object} [object] - If you have an object you'd like to have in the logs throw it in here
   * @param {Object} [metadata] - Additional metadata to include with this log entry
   */
  info: (label = null, message, object = null, metadata = null) => UILogging._log('info', label, message, object, metadata),
  /**
   * Logs a warning to betterstack
   * @param {logging.labels} [label] - Predefined label for the logs, defaults to Generic
   * @param {string} message - The log itself
   * @param {Object} [object] - If you have an object you'd like to have in the logs throw it in here
   * @param {Object} [metadata] - Additional metadata to include with this log entry
   */
  warn: (label = null, message, object = null, metadata = null) => UILogging._log('warn', label, message, object, metadata),
  /**
   * Logs an error to betterstack
   * @param {logging.labels} [label] - Predefined label for the logs, defaults to Generic
   * @param {string} message - The log itself
   * @param {Object} [object] - If you have an object you'd like to have in the logs throw it in here
   * @param {Object} [metadata] - Additional metadata to include with this log entry
   */
  error: (label = null, message, object = null, metadata = null) => UILogging._log('error', label, message, object, metadata),
  /**
   * Logs debug log to the console
   * This only works on stage and local so we don't clog prod
   * @param {logging.labels} [label] - Predefined label for the logs, defaults to Generic
   * @param {string} message - The log itself
   * @param {Object} [object] - If you have an object you'd like to have in the logs throw it in here
   * @param {Object} [metadata] - Additional metadata to include with this log entry
   */
  debug: (label = null, message, object = null, metadata = null) => {
    if (ENV !== 'stage') return;
    UILogging._log('debug', label, message, object, metadata);
  },
  
  /**
   * Set or update global metadata that will be included with all log entries
   * @param {Object} metadata - Metadata to merge with existing global metadata
   */
  setGlobalMetadata: (metadata = {}) => {
    globalMetadata = { ...globalMetadata, ...metadata };
  },

  /**
   * Get the current global metadata
   * @returns {Object} The current global metadata
   */
  getGlobalMetadata: () => ({ ...globalMetadata })
};

export default UILogging;